/** @format */
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import {
  Table,
  Radio,
  Modal,
  Input,
  Select,
  DatePicker,
  Tag,
  Row,
  Col,
  message,
  Button,
  Popconfirm,
  Tooltip,
} from "antd";
import moment from "moment";
import Cookies from "universal-cookie";
import LocationResolver from "../Apiaries/LocationResolver";
import { deleteHiveBatch, disableHiveBatch, revokeHiveBatchLocation } from "../../../../services/apiaryService";

const cookies = new Cookies();
const { Option } = Select;

const currentToken = cookies.get("access_token");

const HIVE_TYPES = [
  "Type A", "Type C", "Hanging"
];

const BeehiveBatches = () => {
  const [batches, setBatches] = useState([]);
  const [factories, setFactories] = useState([]);
  const [loading, setLoading] = useState(false);
  const BASE_URL = process.env.REACT_APP_API_APP_FIELD_URL;

  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({
    factory_id: "",
    supply_date: null,
    total_hives: "",
    hive_type: "",
    owner_type: "HB",
    remarks: "",
  });

  /* ---------------- Deploy Logic ---------------- */

  const [deployModalOpen, setDeployModalOpen] = useState(false);
  const [apiaries, setApiaries] = useState([]);
  const [deployForm, setDeployForm] = useState({
    batch_id: "",
    apiary_id: "",
    hives_deployed: "",
    date: null,
  });

  // Edit batch state
  const [editBatchModal, setEditBatchModal] = useState(false);
  const [editBatchForm, setEditBatchForm] = useState({ batch_id: '', remarks: '' });
  const [editBatchSaving, setEditBatchSaving] = useState(false);

  // Revoke state
  const [revokingKey, setRevokingKey] = useState(null); // tracks which location is being revoked

  const fetchApiaries = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/nector/app/apiaries`, {
        headers: {
          'Content-Type': 'application/json',
          source: 'humblebee_admin',
          Authorization: `Bearer ${currentToken}`,
        },
      });
      setApiaries(res.data || []);
      console.log("🐝 Fetched Apiaries for Batches:", res.data);
    } catch (e) {
      console.error("Apiary fetch error", e);
      // Fallback or error handling
    }
  };

  const openDeploy = (batch) => {
    setDeployForm({
      batch_id: batch.batch_id,
      apiary_id: "",
      hives_deployed: batch.total_hives, // Default to all
      date: moment(),
    });
    setDeployModalOpen(true);
    fetchApiaries();
  };

  const submitDeploy = async () => {
    if (!deployForm.apiary_id || !deployForm.hives_deployed || !deployForm.date) {
      return message.warning("Please fill all deployment fields");
    }

    try {
      // Workaround: Extract user ID from JWT token
      let userId = null;
      try {
        const tokenParts = currentToken.split('.');
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]));
          userId = payload._id || payload.id || payload.userId || payload.user_id;
        }
      } catch (e) {
        console.log('Could not decode token:', e);
      }

      await axios.post(`${BASE_URL}/nector/app/deployments`, {
        batch_id: deployForm.batch_id,
        apiary_id: deployForm.apiary_id,
        hives_deployed: deployForm.hives_deployed,
        deployed_on: deployForm.date.format("YYYY-MM-DD"),
        ...(userId && { deployed_by: userId }) // Include user ID if available
      }, {
        headers: {
          'Content-Type': 'application/json',
          source: 'humblebee_admin',
          Authorization: `Bearer ${currentToken}`,
        }
      });
      message.success("Batch Deployed Successfully!");
      setDeployModalOpen(false);
      fetchBatches(); // Refresh list
    } catch (err) {
      message.error(err?.response?.data?.error || "Deployment Failed");
    }
  };


  /* ---------------- Fetch Factories ---------------- */

  const fetchFactories = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/nector/web/factories`, {
        headers: {
          'Content-Type': 'application/json',
          source: 'humblebee_admin',
          Authorization: `Bearer ${currentToken}`,
        },
      });

      console.log("FACTORIES API RAW RESPONSE:", res.data);

      setFactories(res.data || []);
    } catch (err) {
      console.error("Factory fetch error:", err);
      message.error("Failed to load factories");
    }
  };

  /* ---------------- Fetch Batches ---------------- */

  const fetchBatches = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/nector/web/hive-batches`, {
        headers: {
          'Content-Type': 'application/json',
          source: 'humblebee_admin',
          Authorization: `Bearer ${currentToken}`,
        },
      });
      setBatches(res.data || []);
    } catch {
      message.error("Failed to load batches");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFactories();
    fetchBatches();
  }, []);

  /* ---------------- Edit Batch ---------------- */

  const openEditBatch = (batch) => {
    setEditBatchForm({
      batch_id: batch.batch_id,
      remarks: batch.remarks || '',
      remaining_hives: batch.remaining_hives !== undefined ? batch.remaining_hives : batch.total_hives,
      total_hives: batch.total_hives,
      factory_id: batch.factory_id,
      hive_type: batch.hive_type,
      supply_date: batch.supply_date,
      owner_type: batch.owner_type
    });
    setEditBatchModal(true);
  };

  const submitEditBatch = async () => {
    setEditBatchSaving(true);
    try {
      await axios.patch(
        `${BASE_URL}/nector/web/hive-batches/${editBatchForm.batch_id}`,
        {
          remarks: editBatchForm.remarks,
          remaining_hives: editBatchForm.remaining_hives,
          total_hives: editBatchForm.total_hives,
          factory_id: editBatchForm.factory_id,
          hive_type: editBatchForm.hive_type,
          supply_date: moment(editBatchForm.supply_date).format("YYYY-MM-DD"),
          owner_type: editBatchForm.owner_type
        },
        {
          headers: {
            'Content-Type': 'application/json',
            source: 'humblebee_admin',
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );
      message.success('Batch updated successfully');
      setEditBatchModal(false);
      fetchBatches();
    } catch (err) {
      message.error(err?.response?.data?.error || 'Update failed');
    } finally {
      setEditBatchSaving(false);
    }
  };

  /* ---------------- Revoke Deployment ---------------- */

  const handleRevoke = async (batchId, apiaryId, internalId) => {
    // We favor internalId (_id) to avoid casing issues with batch_id
    const targetId = internalId || batchId;
    const revokeKey = `${targetId}_${apiaryId}`;
    setRevokingKey(revokeKey);

    try {
      // Step 1: Call the Universal Revoke endpoint
      await revokeHiveBatchLocation(targetId, apiaryId);
      
      message.success('Hives revoked from apiary successfully');
      fetchBatches();
    } catch (err) {
      console.error('Revoke error:', err);
      message.error(err?.response?.data?.error || 'Failed to revoke hives');
    } finally {
      setRevokingKey(null);
    }
  };

  /* ---------------- Delete Batch ---------------- */

  const handleArchiveBatch = async (batchId) => {
    try {
      await disableHiveBatch(batchId);
      message.success("Batch archived successfully");
      fetchBatches();
    } catch (err) {
      message.error(err?.response?.data?.error || "Failed to archive batch. Ensure no active deployments exist.");
    }
  };

  const handlePermanentDelete = async (batchId) => {
    try {
      await deleteHiveBatch(batchId);
      message.success("Batch deleted permanently");
      fetchBatches();
    } catch (err) {
      if (err?.response?.status === 409) {
        const linked = err.response.data?.linked_records || [];
        message.error(`Cannot delete permanently: ${linked.join(", ")} found. Use Archive instead.`);
      } else {
        message.error(err?.response?.data?.error || "Failed to delete batch");
      }
    }
  };

  /* ---------------- Create ---------------- */

  const openAdd = () => {
    setForm({
      factory_id: "",
      supply_date: null,
      total_hives: "",
      hive_type: "",
      owner_type: "HB",
      remarks: "",
    });
    setModalOpen(true);
  };

  const submit = async () => {
    if (
      !form.factory_id ||
      !form.supply_date ||
      !form.total_hives ||
      !form.hive_type
    ) {
      return message.warning("Fill all required fields");
    }

    try {
      await axios.post(
        `${BASE_URL}/nector/web/hive-batches`,
        {
          ...form,
          supply_date: form.supply_date.format("YYYY-MM-DD"),
        },
        {
          headers: {
            'Content-Type': 'application/json',
            source: 'humblebee_admin',
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );

      message.success("Batch created");
      setModalOpen(false);
      fetchBatches();
    } catch (err) {
      message.error(err?.response?.data?.error || "Create failed");
    }
  };

  /* ---------------- Table ---------------- */

  const columns = [
    { title: "Factory ID", dataIndex: "factory_id" },
    { title: "Batch ID", dataIndex: "batch_id" },
    {
      title: "Supply Date",
      dataIndex: "supply_date",
      render: v => moment(v).format("DD MMM YYYY"),
    },
    { title: "Total Hives", dataIndex: "total_hives" },
    { title: "Hive Type", dataIndex: "hive_type" },
    {
      title: "Remarks",
      dataIndex: "remarks",
      render: v => v || <span style={{ color: '#ccc' }}>-</span>,
    },
    {
      title: "Owner",
      dataIndex: "owner_type",
      render: v => (v === "HB" ? "Humble Bee" : "Community"),
    },
    {
      title: "Current Locations",
      render: (row) => {
        if (row.current_locations && row.current_locations.length > 0) {
          return (
            <div>
              {row.current_locations.map((loc, idx) => {
                const apiary = apiaries.find(a => a.apiary_id === loc.apiary_id);
                const revokeKey = `${row.batch_id}_${loc.apiary_id}`;
                const isRevoking = revokingKey === revokeKey;
                return (
                  <div key={idx} style={{ marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ flex: 1 }}>
                      <Tag color="geekblue">
                        {apiary ? apiary.apiary_name : loc.apiary_id}
                      </Tag>
                      {loc.hives_deployed && (
                        <span style={{ fontSize: 11, color: '#666' }}>({loc.hives_deployed} hives)</span>
                      )}
                      {apiary && <div style={{ fontSize: 10, color: '#888' }}>
                        {apiary.district && !/^-?\d+(\.\d+)?$/.test(apiary.district) ? apiary.district : ''}
                        {apiary.state && !/^-?\d+(\.\d+)?$/.test(apiary.state)
                          ? (apiary.district && !/^-?\d+(\.\d+)?$/.test(apiary.district) ? ', ' : '') + apiary.state
                          : <LocationResolver
                            lat={apiary.geo_location?.lat || apiary.latitude}
                            lng={apiary.geo_location?.lng || apiary.longitude}
                            value={apiary.state}
                            field="state"
                            apiaryId={apiary.apiary_id}
                          />
                        }
                      </div>}
                    </div>
                    <Popconfirm
                      title="Revoke these hives from apiary?"
                      description="Hives will be returned to batch storage pool."
                      onConfirm={() => handleRevoke(row.batch_id, loc.apiary_id, row._id)}
                      okText="Yes"
                      cancelText="No">
                      <Button
                        size="small"
                        icon={<i className="fa fa-undo"></i>}
                        loading={isRevoking}
                        style={{ marginLeft: 8 }}>
                        Revoke
                      </Button>
                    </Popconfirm>
                  </div>
                );
              })}
            </div>
          );
        }
        return <span style={{ color: '#ccc' }}>-</span>;
      }
    },
    {
      title: "Status",
      render: (row) => {
        // If deployed, show "Deployed", else "In Storage"
        // Assuming backend updates status logic
        // Or check if current_locations exists and is not empty
        return <Tag color={row.current_locations?.length > 0 ? "green" : "orange"}>
          {row.current_locations?.length > 0 ? "Deployed" : "In Storage"}
        </Tag>
      }
    },
    {
      title: "Action",
      render: (row) => {
        const isDeployed = row.current_locations?.length > 0;
        return (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <Button size="small" onClick={() => openEditBatch(row)}>
              <i className="fa fa-pencil" style={{ marginRight: 4 }}></i> Edit
            </Button>
            
            {!isDeployed ? (
              <Button type="primary" size="small" onClick={() => openDeploy(row)}>
                Deploy
              </Button>
            ) : (
              <Tooltip title="Batch is currently deployed. Revoke hives to archive.">
                <span style={{ color: '#00b894', fontSize: '12px', fontWeight: 600 }}>
                  <i className="fa fa-ship mr-1"></i> Deployed
                </span>
              </Tooltip>
            )}

            <Popconfirm
              title="Archive this batch?"
              description="This will hide the batch but keep history. Only allowed if NOT deployed."
              onConfirm={() => handleArchiveBatch(row._id)}
              okText="Archive"
              cancelText="Cancel"
            >
              <Button size="small" disabled={isDeployed}>
                <i className="fa fa-archive" style={{ marginRight: 4 }}></i> Archive
              </Button>
            </Popconfirm>

            <Popconfirm
              title="PERMANENT DELETE?"
              description="This will completely remove it from MongoDB. Will fail if any history exists."
              onConfirm={() => handlePermanentDelete(row._id)}
              okText="Delete Permanently"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
            >
              <Button size="small" danger icon={<i className="fa fa-trash" style={{ marginRight: 4 }}></i>}>
                Delete
              </Button>
            </Popconfirm>
          </div>
        );
      }
    }
  ];

  /* ---------------- UI ---------------- */

  useEffect(() => {
    fetchFactories();
    fetchBatches();
    fetchApiaries(); // Fetch apiaries on mount to map names
  }, []);

  const [batchFilters, setBatchFilters] = useState({ status: 'ALL', hiveType: 'ALL', search: '', dateRange: [] });

  const getFilteredBatches = () => {
    return batches.filter(batch => {
      // Search filter (Batch ID or Remarks)
      const searchLower = batchFilters.search.toLowerCase();
      const matchesSearch = !batchFilters.search ||
        (batch.batch_id?.toLowerCase().includes(searchLower)) ||
        (batch.remarks?.toLowerCase().includes(searchLower));

      // Status filter
      let matchesStatus = true;
      const isDeployed = batch.current_locations?.length > 0;
      if (batchFilters.status === 'DEPLOYED') {
        matchesStatus = isDeployed;
      } else if (batchFilters.status === 'STORAGE') {
        matchesStatus = !isDeployed;
      }

      // Hive Type filter
      let matchesHiveType = true;
      if (batchFilters.hiveType && batchFilters.hiveType !== 'ALL') {
        matchesHiveType = batch.hive_type === batchFilters.hiveType;
      }

      // Date filter
      let matchesDate = true;
      if (batchFilters.dateRange && batchFilters.dateRange.length === 2 && batchFilters.dateRange[0] && batchFilters.dateRange[1]) {
        const supplyMoment = moment(batch.supply_date);
        matchesDate = supplyMoment.isBetween(batchFilters.dateRange[0], batchFilters.dateRange[1], 'day', '[]');
      }

      return matchesSearch && matchesStatus && matchesHiveType && matchesDate;
    });
  };

  const filteredBatches = getFilteredBatches();

  // Summary by Hive Type across all batches
  const hiveTypeSummary = useMemo(() => {
    const summary = {};
    let totalHivesAll = 0;
    batches.forEach(b => {
      const type = b.hive_type || 'Other';
      const count = Number(b.total_hives) || 0;
      summary[type] = (summary[type] || 0) + count;
      totalHivesAll += count;
    });
    return { summary, totalHivesAll };
  }, [batches]);

  return (
    <div style={{ padding: 24, marginLeft: 250 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2>Total Beehive Batches</h2>
        <button className="btn add-new-scheme-btn" onClick={openAdd}>
          Add New Batch
        </button>
      </div>

      {/* Top Summary Banner: Total Hives Shipped to Date by Type */}
      <div style={{
        display: 'flex',
        gap: '20px',
        flexWrap: 'wrap',
        marginBottom: '20px',
        background: '#ffffff',
        padding: '16px 20px',
        borderRadius: '12px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
        border: '1px solid #e8e8e8',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', paddingRight: '20px', borderRight: '2px solid #f0f0f0' }}>
          <i className="fa fa-cubes" style={{ fontSize: '26px', color: '#1890ff' }}></i>
          <div>
            <div style={{ fontSize: '11px', color: '#8c8c8c', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Total Hives Shipped to Date
            </div>
            <div style={{ fontSize: '22px', fontWeight: 800, color: '#262626' }}>
              {hiveTypeSummary.totalHivesAll.toLocaleString()} <span style={{ fontSize: '13px', color: '#8c8c8c', fontWeight: 400 }}>Hives</span>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', flex: 1, alignItems: 'center' }}>
          {Object.entries(hiveTypeSummary.summary).map(([type, count]) => (
            <div key={type} style={{
              background: '#fafafa',
              padding: '10px 18px',
              borderRadius: '8px',
              border: '1px solid #f0f0f0',
              minWidth: '140px'
            }}>
              <div style={{ fontSize: '12px', color: '#595959', fontWeight: 600 }}>{type}</div>
              <div style={{ fontSize: '18px', fontWeight: 700, color: '#1890ff' }}>
                {count.toLocaleString()} <span style={{ fontSize: '12px', color: '#8c8c8c', fontWeight: 400 }}>hives</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Filter Section */}
      <Row gutter={[16, 16]} style={{ marginBottom: 20, background: '#f9f9f9', padding: '16px', borderRadius: '8px', border: '1px solid #eee' }}>
        <Col xs={24} md={6}>
          <div style={{ marginBottom: 4, fontSize: '12px', fontWeight: '600' }}>Search</div>
          <Input
            placeholder="Search by Batch ID or Remarks..."
            value={batchFilters.search}
            onChange={e => setBatchFilters({ ...batchFilters, search: e.target.value })}
            allowClear
            prefix={<i className="fa fa-search" style={{ color: '#bfbfbf', marginRight: 4 }}></i>}
          />
        </Col>
        <Col xs={24} md={5}>
          <div style={{ marginBottom: 4, fontSize: '12px', fontWeight: '600' }}>Status</div>
          <Select
            style={{ width: '100%' }}
            value={batchFilters.status}
            onChange={val => setBatchFilters({ ...batchFilters, status: val })}
          >
            <Option value="ALL">All Status</Option>
            <Option value="DEPLOYED">Deployed</Option>
            <Option value="STORAGE">In Storage</Option>
          </Select>
        </Col>
        <Col xs={24} md={5}>
          <div style={{ marginBottom: 4, fontSize: '12px', fontWeight: '600' }}>Hive Type</div>
          <Select
            style={{ width: '100%' }}
            value={batchFilters.hiveType}
            onChange={val => setBatchFilters({ ...batchFilters, hiveType: val })}
          >
            <Option value="ALL">All Hive Types</Option>
            {Object.keys(hiveTypeSummary.summary).map(type => (
              <Option key={type} value={type}>{type}</Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} md={8}>
          <div style={{ marginBottom: 4, fontSize: '12px', fontWeight: '600' }}>Supply Date Range</div>
          <DatePicker.RangePicker
            style={{ width: '100%' }}
            value={batchFilters.dateRange}
            onChange={vals => setBatchFilters({ ...batchFilters, dateRange: vals })}
            format="DD MMM YYYY"
          />
        </Col>
      </Row>

      <div style={{ marginBottom: 12, fontWeight: 'bold' }}>
        Showing {filteredBatches.length} of {batches.length} batches
      </div>

      <Table
        rowKey="batch_id"
        columns={columns}
        dataSource={filteredBatches}
        loading={loading}
        pagination={{
          defaultPageSize: 100,
          pageSizeOptions: ['100', '200', '500', '1000'],
          showSizeChanger: true,
        }}
      />

      {/* ---------------- Create Batch Modal ---------------- */}

      <Modal
        title="Add Batch"
        visible={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={submit}
        okText="Submit"
        okButtonProps={{
          className: "btn add-new-scheme-btn"
        }}
      >
        <Row >
          <Col span={12}>
            <label>Factory *</label>
            <Select
              style={{ width: "100%" }}
              value={form.factory_id}
              onChange={(v) => setForm({ ...form, factory_id: v })}
              placeholder="Select Factory"
            >
              {factories
                .filter(f => f.is_active === true) // IMPORTANT
                .map(f => (
                  <Option key={f.factory_id} value={f.factory_id}>
                    {f.factory_id} — {f.name}
                  </Option>
                ))}
            </Select>
          </Col>

          <Col span={12}>
            <label>Supply Date *</label>
            <DatePicker
              style={{ width: "100%" }}
              value={form.supply_date}
              onChange={d => setForm({ ...form, supply_date: d })}
            />
          </Col>

          <Col span={12}>
            <label>Total Hives *</label>
            <Input
              type="number"
              value={form.total_hives}
              onChange={e =>
                setForm({ ...form, total_hives: Number(e.target.value) })
              }
            />
          </Col>

          <Col span={12} >
            <label>Hive Type *</label>
            <Select
              style={{ width: "100%" }}
              value={form.hive_type}
              onChange={v => setForm({ ...form, hive_type: v })}
            >
              {HIVE_TYPES.map(t => (
                <Option key={t}>{t}</Option>
              ))}
            </Select>
          </Col>

          <Col span={24} style={{ marginTop: 12 }}>
            <label>Owner *</label>
            <Radio.Group
              value={form.owner_type}
              onChange={e =>
                setForm({ ...form, owner_type: e.target.value })
              }

            >
              <Radio value="HB" >Humble Bee</Radio>
              <Radio value="Community">Community</Radio>
            </Radio.Group>

          </Col>

          <Col span={24} style={{ marginTop: 12 }}>
            <label>Remarks</label>
            <Input.TextArea
              rows={3}
              value={form.remarks}
              onChange={e =>
                setForm({ ...form, remarks: e.target.value })
              }
            />
          </Col>
        </Row>
      </Modal>

      {/* ---------------- Deploy Batch Modal ---------------- */}
      <Modal
        title="Deploy Batch to Apiary"
        visible={deployModalOpen}
        onCancel={() => setDeployModalOpen(false)}
        onOk={submitDeploy}
        okText="Deploy"
        okButtonProps={{ className: "btn add-new-scheme-btn" }}
      >
        <div style={{ marginBottom: 12 }}>
          <strong>Batch ID:</strong> {deployForm.batch_id}
        </div>

        <Row gutter={[16, 16]}>
          <Col span={24}>
            <label>Select Apiary *</label>
            <Select
              style={{ width: "100%" }}
              placeholder="Select Destination Apiary"
              value={deployForm.apiary_id}
              showSearch
              optionFilterProp="children"
              onChange={(v) => setDeployForm({ ...deployForm, apiary_id: v })}
            >
              {apiaries.map(a => (
                <Option key={a.apiary_id} value={a.apiary_id}>
                  {a.apiary_name || a.name || a.apiary_id} ({a.state}, {a.district})
                </Option>
              ))}
            </Select>
          </Col>

          <Col span={12}>
            <label>Hives to Deploy *</label>
            <Input
              type="number"
              value={deployForm.hives_deployed}
              onChange={e => setDeployForm({ ...deployForm, hives_deployed: e.target.value })}
            />
          </Col>

          <Col span={12}>
            <label>Deployment Date *</label>
            <DatePicker
              style={{ width: "100%" }}
              value={deployForm.date}
              onChange={d => setDeployForm({ ...deployForm, date: d })}
            />
          </Col>
        </Row>
      </Modal>

      {/* ---------------- Edit Batch Modal ---------------- */}
      <Modal
        title="Edit Batch"
        visible={editBatchModal}
        onCancel={() => setEditBatchModal(false)}
        onOk={submitEditBatch}
        okText={editBatchSaving ? "Saving..." : "Save"}
        okButtonProps={{
          className: "btn add-new-scheme-btn",
          disabled: editBatchSaving,
        }}
      >
        <div style={{ marginBottom: 12 }}>
          <strong>Batch ID:</strong> {editBatchForm.batch_id}
        </div>
        <div>
          <label>Remarks</label>
          <Input.TextArea
            rows={4}
            value={editBatchForm.remarks}
            onChange={e => setEditBatchForm({ ...editBatchForm, remarks: e.target.value })}
            placeholder="Enter remarks..."
          />
        </div>
      </Modal>

    </div>
  );
};

export default BeehiveBatches;
